import React, { useState } from 'react';
import { AttendeeEntry, WaitlistEntry } from '../../types/models';
import { initials } from '../../utils/strings';

interface AttendeeListProps {
  attendees: AttendeeEntry[];
  waitlist?: WaitlistEntry[];
}

function Section({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <span className="font-medium text-gray-800 text-sm">
          {title} <span className="text-gray-500">({count})</span>
        </span>
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && <div className="divide-y divide-gray-100">{children}</div>}
    </div>
  );
}

const AttendeeList: React.FC<AttendeeListProps> = ({ attendees, waitlist }) => {
  return (
    <div className="space-y-2 mt-4">
      <Section title="Attendees" count={attendees.length}>
        {attendees.length === 0 ? (
          <p className="px-4 py-3 text-sm text-gray-500">No attendees yet.</p>
        ) : (
          attendees.map(a => (
            <div key={a.userId} className="flex items-center gap-3 px-4 py-2">
              <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                {initials(a.name)}
              </div>
              <span className="text-sm text-gray-800">{a.name}</span>
            </div>
          ))
        )}
      </Section>

      {waitlist !== undefined && (
        <Section title="Waitlist" count={waitlist.length}>
          {waitlist.length === 0 ? (
            <p className="px-4 py-3 text-sm text-gray-500">No one on the waitlist.</p>
          ) : (
            waitlist.map(w => (
              <div key={w.userId} className="flex items-center gap-3 px-4 py-2">
                <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                  {w.position}
                </div>
                <span className="text-sm text-gray-800">{w.name}</span>
              </div>
            ))
          )}
        </Section>
      )}
    </div>
  );
};

export default AttendeeList;
